/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useWebSocket.ts
 * PURPOSE: WebSocket consumer hook — connects to /ws/{user_id} for real-time dashboard updates
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 04 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance, Rule 7: Performance SLAs (<500ms WS latency)
 *
 * @business-context
 * - Domain: Real-Time Updates, WebSocket Integration
 * - Impact: Employee Dashboard live updates (<500ms per SPEC-0407 AC-004)
 * - Stakeholders: Employees (71), Managers
 *
 * @technical-architecture
 * - Layer: Data Transport Layer, Real-Time Communication
 * - Pattern: WebSocket Consumer, Auto-Reconnect, TanStack Query Invalidation
 * - Dependencies: Native WebSocket API, TanStack Query (queryClient.invalidateQueries)
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0407-manager-dashboards-design.md (§2.2, AC-004)
 * - Backend: backend/app/main.py:419 (/ws/{user_id} endpoint)
 * - Backend: backend/app/services/realtime_sync.py (SyncEvent enum)
 * - Decision: docs/02-design/18-adrs/06-decisions/DD-0026-employee-dashboard-realtime-offline-architecture.md
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { DASHBOARD_QUERY_KEYS } from './queryKeys';

// ============================================================================
// Types — aligned with backend/app/services/realtime_sync.py SyncEvent enum
// ============================================================================

/** Backend SyncEvent types that trigger dashboard refresh */
type SyncEventType =
  | 'attendance_update'
  | 'schedule_change'
  | 'dashboard_update'
  | 'task_assigned'
  | 'notification'
  | 'connection'
  | 'pong';

interface WSMessage {
  type: SyncEventType;
  data?: any;
  timestamp?: number;
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  lastEvent: WSMessage | null;
  /** True when max reconnect attempts exhausted — consumer should enable polling fallback */
  isFallbackMode: boolean;
}

// ============================================================================
// Config — SPEC-0407 §2.2 Performance Targets
// ============================================================================

const WS_RECONNECT_DELAY_MS = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 10;
const WS_PING_INTERVAL_MS = 30_000;

function getWsBaseUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const configuredUrl = import.meta.env.VITE_API_BASE_URL;
  if (configuredUrl && configuredUrl !== '') {
    const url = new URL(configuredUrl);
    return `${protocol}//${url.host}`;
  }
  return `${protocol}//${window.location.host}`;
}

// ============================================================================
// Hook
// ============================================================================

export function useWebSocket(): UseWebSocketReturn {
  const { user, accessToken } = useAuthStore();
  const queryClient = useQueryClient();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<WSMessage | null>(null);
  /** MINOR #5: true when max reconnect attempts exhausted — signals fallback to polling */
  const [isFallbackMode, setIsFallbackMode] = useState(false);

  // S95L-009: Play notification sound for alert events (respects browser autoplay policy)
  const playAlertSound = useCallback(() => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880; // A5 note
      gain.gain.value = 0.15;    // Subtle volume
      osc.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.stop(ctx.currentTime + 0.3);
      osc.onended = () => ctx.close(); // CPO F1: prevent AudioContext leak
    } catch {
      // AudioContext blocked by browser policy — silent fallback
    }
  }, []);

  // ── Map backend event → TanStack query invalidation ──
  const handleMessage = useCallback((msg: WSMessage) => {
    setLastEvent(msg);

    switch (msg.type) {
      case 'attendance_update':
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.attendanceStatus });
        playAlertSound();
        break;
      case 'schedule_change':
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.todaySchedule });
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.tomorrowSchedule });
        break;
      case 'task_assigned':
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.pendingTasks });
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.taskSummary });
        playAlertSound();
        break;
      case 'dashboard_update':
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all });
        break;
      case 'pong':
      case 'connection':
        // Keep-alive / handshake — no query invalidation
        break;
    }
  }, [queryClient, playAlertSound]);

  // ── Connect ──
  const connect = useCallback(() => {
    if (!accessToken || !user?.id) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = `${getWsBaseUrl()}/ws/${user.id}?token=${accessToken}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;

      // Start ping keep-alive (SPEC-0407 §2.2: 30s fallback)
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, WS_PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data);
        handleMessage(msg);
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Auto-reconnect with backoff; fallback to polling-only when exhausted (MINOR #5)
      if (reconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
        const delay = WS_RECONNECT_DELAY_MS * Math.min(reconnectAttemptsRef.current + 1, 5);
        reconnectAttemptsRef.current += 1;
        setTimeout(connect, delay);
      } else {
        setIsFallbackMode(true);
      }
    };

    ws.onerror = () => {
      // onclose will handle reconnect
    };

    wsRef.current = ws;
  }, [accessToken, user?.id, handleMessage]);

  // ── Lifecycle ──
  useEffect(() => {
    connect();

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        reconnectAttemptsRef.current = WS_MAX_RECONNECT_ATTEMPTS; // Prevent reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { isConnected, lastEvent, isFallbackMode };
}
